import { isChannel, type Channel } from '../../../shared/send-schema.js';
import type { ApiFilteredBookingRow } from '../../lib/actions.js';

/**
 * ⭐⭐ Board 2812 M3 (plan step 3, R-3) - THE ACTION TABLE, AND NOTHING ELSE.
 *
 * Each To do card offers only what the server would accept, read from the ROW'S
 * OWN SERVER FLAGS (M1's `/api/todo-piles` rows): `can_mark_travelled` is
 * `markTravelledRefusal(...) === null`, `can_not_go_ahead` the not-going-ahead
 * refusal, and `quote_sent_channel` the channel the latest quote went out on.
 * ⛔ THE PILE RULES LIVE ON THE SERVER (`server/todo-piles.ts`): this file never
 * decides which pile a row is in, and never re-derives a flag from dates, money or
 * status. A taxi-passed run arrives with `can_mark_travelled: false`, so it is
 * offered no Mark as travelled here without this file knowing about taxis.
 * ⛔ ONE GOLD AT MOST, AND THE DESTRUCTIVE IN THE END SLOT (R-2812-d): `primary` is
 * the gold, `end` the one destructive, which confirms in place before it acts.
 */
export type TodoPileName = 'unsent' | 'wait' | 'soon' | 'outcome';

export type TodoAction =
  /** Open the saved, unsent quote on its booking, where it is sent (Q246). */
  | 'open-quote'
  /** `sendQuoteRequest` on the row's own `quote_sent_channel`, with the live deposit link. */
  | 'send-again'
  | 'open-booking'
  /** `markRunTravelled`. */
  | 'mark-travelled'
  /** `cancelBookingRun` in its not-going-ahead mode. */
  | 'not-going-ahead';

export type ActionPlan = {
  primary: TodoAction | null;
  quiet: TodoAction[];
  end: TodoAction | null;
};

export type ActionRowFacts = Pick<
  ApiFilteredBookingRow,
  'quote_sent_channel' | 'can_mark_travelled' | 'can_not_go_ahead'
>;

/** The channel "Send the quote again" re-sends on, or null when the quote only ever
 *  went out by copy (then no send is offered: Open the booking and Not going ahead,
 *  as `web-todo` draws them). */
export function resendChannel(r: Pick<ActionRowFacts, 'quote_sent_channel'>): Channel | null {
  return isChannel(r.quote_sent_channel) ? r.quote_sent_channel : null;
}

/**
 * `channelOn` is the registry's answer to "is this channel switched on" (`isChannelConfigured`):
 * the send route refuses a channel that is off (`not-configured`, measured on the M3 seat:
 * "Text messages are not switched on. Nothing was sent to the customer."), so a Wait card
 * whose quote went out on a channel now off is offered no "Send the quote again".
 */
export function actionsFor(pile: TodoPileName, r: ActionRowFacts, channelOn: (c: Channel) => boolean = () => true): ActionPlan {
  const end: TodoAction | null = r.can_not_go_ahead ? 'not-going-ahead' : null;
  switch (pile) {
    case 'unsent':
      return { primary: 'open-quote', quiet: [], end };
    case 'wait': {
      const channel = resendChannel(r);
      return { primary: channel === null || !channelOn(channel) ? null : 'send-again', quiet: ['open-booking'], end };
    }
    case 'soon':
      return { primary: null, quiet: ['open-booking'], end: null };
    case 'outcome':
      return { primary: r.can_mark_travelled ? 'mark-travelled' : null, quiet: ['open-booking'], end };
  }
}

/** The words on each action's control (R-11: every new word is listed for Vicky). */
export const ACTION_WORDS: Readonly<Record<TodoAction, string>> = {
  'open-quote': 'Open the quote',
  'send-again': 'Send the quote again',
  'open-booking': 'Open the booking',
  'mark-travelled': 'Mark as travelled',
  'not-going-ahead': 'Not going ahead',
};
